import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  ContactRound,
  ExternalLink,
  History,
  Map,
  Maximize2,
  MessageSquareShare,
  Minimize2,
  Phone,
  Printer,
  Settings2,
  UserX,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { usePnrUtilities } from './use-pnr-utilities';

// The passenger card's «Más acciones ▾» (PO 2026-10-31: «las acciones raras van a "Más
// acciones", no seis botones en el pie»). Everything the old footer carried is here, in
// three groups: tell the passenger (travel pack, send info, call, fix the contact), the
// printed vouchers, and this PNR (activity, open the case, manual override, size, and —
// last, destructive — exclude). The escapes drop off once ticketed (committed in the PSS).
// Comms ride `usePnrUtilities`, owned by the card so its dialogs outlive the menu.

export type PnrUtilities = ReturnType<typeof usePnrUtilities>;

const ITEM = 'min-h-10 gap-2';

export function PnrMoreActions({
  caseUrn,
  ticketed,
  onManualOverride,
  manualOverrideEnabled = true,
  onExclude,
  excludePending,
  utilities,
  onOpenActivity,
  maximized,
  onToggleMaximize,
}: {
  /** The case URN — the Open-case link target. */
  caseUrn: string;
  /** The PSS ticket is issued → the escape actions no longer apply. */
  ticketed?: boolean;
  onManualOverride: () => void;
  manualOverrideEnabled?: boolean;
  onExclude: () => void;
  /** Disables Exclude while the exclude mutation is in flight. */
  excludePending?: boolean;
  /** The comms + vouchers of this PNR; absent = those groups are not offered. */
  utilities?: PnrUtilities | undefined;
  onOpenActivity?: (() => void) | undefined;
  maximized?: boolean | undefined;
  onToggleMaximize?: (() => void) | undefined;
}) {
  const { t } = useTranslation('pnrActions');
  const u = utilities;
  const label = t('more.trigger');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="h-10 shrink-0 gap-1.5 px-3.5 text-sm"
          aria-label={label}
          data-testid="drawer-advanced-actions"
        >
          {label}
          <ChevronDown className="h-4 w-4" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[70dvh] min-w-[15rem] overflow-y-auto">
        {u ? (
          <>
            <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {t('utility.send.trigger')}
            </DropdownMenuLabel>
            <DropdownMenuItem className={ITEM} disabled={u.resendPending} onSelect={() => u.runResend()} data-testid="pnr-resend-preferred">
              <MessageSquareShare className="h-4 w-4" aria-hidden />
              {t('utility.send.preferred')}
            </DropdownMenuItem>
            <DropdownMenuItem className={ITEM} disabled={u.resendPending} onSelect={() => u.runResend('WHATSAPP')} data-testid="pnr-resend-whatsapp">
              <MessageSquareShare className="h-4 w-4" aria-hidden />
              {t('utility.send.whatsapp')}
            </DropdownMenuItem>
            <DropdownMenuItem className={ITEM} disabled={u.resendPending} onSelect={() => u.runResend('SMS')} data-testid="pnr-resend-sms">
              <MessageSquareShare className="h-4 w-4" aria-hidden />
              {t('utility.send.sms')}
            </DropdownMenuItem>
            <DropdownMenuItem className={ITEM} disabled={u.resendPending} onSelect={() => u.runResend('EMAIL')} data-testid="pnr-resend-email">
              <MessageSquareShare className="h-4 w-4" aria-hidden />
              {t('utility.send.email')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className={ITEM} disabled={u.resendPending} onSelect={u.runCall} data-testid="pnr-call-passenger">
              <Phone className="h-4 w-4" aria-hidden />
              {t('utility.call.label')}
            </DropdownMenuItem>
            <DropdownMenuItem className={ITEM} onSelect={u.openContact} data-testid="pnr-update-contact">
              <ContactRound className="h-4 w-4" aria-hidden />
              {t('utility.updateContact')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className={ITEM}
              disabled={!u.hasAnythingToPrint}
              onSelect={() => u.print('travel-pack')}
              data-testid="pnr-print-travel-pack"
            >
              <Printer className="h-4 w-4" aria-hidden />
              {t('utility.print.travelPack')}
            </DropdownMenuItem>
            <DropdownMenuItem
              className={ITEM}
              disabled={!u.hasFlight}
              onSelect={() => u.print('flight-voucher')}
              data-testid="pnr-print-flight-voucher"
            >
              <Printer className="h-4 w-4" aria-hidden />
              {t('utility.print.flightVoucher')}
            </DropdownMenuItem>
            <DropdownMenuItem
              className={ITEM}
              disabled={!u.hasHotelReservation}
              onSelect={() => u.print('hotel-voucher')}
              data-testid="pnr-print-hotel-voucher"
            >
              <Printer className="h-4 w-4" aria-hidden />
              {t('utility.print.hotelVoucher')}
            </DropdownMenuItem>
            <DropdownMenuItem
              className={ITEM}
              disabled={!u.hasHotelReservation}
              onSelect={() => u.print('hotel-directions')}
              data-testid="pnr-print-directions"
            >
              <Map className="h-4 w-4" aria-hidden />
              {t('utility.print.directions')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        ) : null}
        <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t('more.groupPnr')}
        </DropdownMenuLabel>
        {onOpenActivity ? (
          <DropdownMenuItem className={ITEM} onSelect={onOpenActivity} data-testid="pnr-open-activity">
            <History className="h-4 w-4" aria-hidden />
            {t('more.activity')}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem asChild className={ITEM}>
          <Link to={`/cases/${encodeURIComponent(caseUrn)}`} data-testid="drawer-open-case">
            <ExternalLink className="h-4 w-4" aria-hidden />
            {t('actionBar.openCase')}
          </Link>
        </DropdownMenuItem>
        {!ticketed && manualOverrideEnabled ? (
          <DropdownMenuItem className={ITEM} onSelect={onManualOverride} data-testid="drawer-override">
            <Settings2 className="h-4 w-4" aria-hidden />
            {t('actionBar.manualOverride')}
          </DropdownMenuItem>
        ) : null}
        {onToggleMaximize ? (
          <DropdownMenuItem className={ITEM} onSelect={onToggleMaximize} data-testid="pnr-maximize">
            {maximized ? <Minimize2 className="h-4 w-4" aria-hidden /> : <Maximize2 className="h-4 w-4" aria-hidden />}
            {maximized ? t('more.restore') : t('more.maximize')}
          </DropdownMenuItem>
        ) : null}
        {!ticketed ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className={`${ITEM} text-destructive focus:text-destructive`}
              onSelect={onExclude}
              disabled={!!excludePending}
              data-testid="drawer-exclude"
            >
              <UserX className="h-4 w-4" aria-hidden />
              {t('actionBar.excludePnr')}
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
